import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CarTechProfileDetail } from '@/features/cars/api/carReportsApi';
import { CarView } from '@/features/cars/utils/carDetailMapping';
import { getImageAreasByVariant } from './car_schema_coords_variants';

interface InteractiveCarImageProps {
  details: CarTechProfileDetail[];
  selectedDetails: string[]; // Массив ID выбранных деталей
  onDetailSelect: (detailId: string) => void;
  onDetailDeselect: (detailId: string) => void;
  availableDetails?: CarTechProfileDetail[]; // Детали, которые можно выбрать
  onDetailFocus?: (detailId: string) => void; // Callback для фокусировки поля комментария
}

// Интерфейс для области на изображении
interface ImageArea {
  id: string;
  title: string;
  coords: string; // Координаты в формате "x1,y1,x2,y2,..." для shape="poly"
  view: CarView;
  detailKeywords: string[]; // Ключевые слова для поиска соответствующих деталей
}

// Области на изображении car_schema.png теперь загружаются динамически из car_schema_coords_variants.ts
// Используйте URL параметр ?coords=kangoo|polo|toyota|sv|xl|backup для переключения между вариантами

// Базовый размер изображения
const BASE_IMAGE_WIDTH = 779;
const BASE_IMAGE_HEIGHT = 413;

const InteractiveCarImage: React.FC<InteractiveCarImageProps> = ({
  details,
  selectedDetails,
  onDetailSelect,
  onDetailDeselect,
  availableDetails,
  onDetailFocus,
}) => {
  const [hoveredDetail, setHoveredDetail] = useState<string | null>(null);
  const [hoveredArea, setHoveredArea] = useState<string | null>(null);
  const [filteredArea, setFilteredArea] = useState<string | null>(null); // Область для фильтрации списка
  const [searchQuery, setSearchQuery] = useState<string>(''); // Поисковый запрос
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [imageDisplaySize, setImageDisplaySize] = useState<{ width: number; height: number; left: number; top: number } | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const imageRef = useRef<HTMLImageElement>(null);
  const mapRef = useRef<HTMLMapElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Получаем вариант координат из URL параметра (для тестирования разных вариантов)
  const urlParams = new URLSearchParams(window.location.search);
  const coordsVariant = urlParams.get('coords') || 'sv'; // По умолчанию используем sv
  const IMAGE_AREAS = useMemo(() => {
    const areas = getImageAreasByVariant(coordsVariant);
    console.log(`📍 Используется вариант координат: ${coordsVariant.toUpperCase()} (${areas.length} областей)`);
    return areas as ImageArea[];
  }, [coordsVariant]);

  // allDetails - все детали для отображения в списке (исключая выбранные)
  const allDetails = availableDetails || details;

  // allDetailsForMapping - все детали включая выбранные, для маппинга и поиска областей
  // Используем details как основной источник, так как он содержит все детали
  const allDetailsForMapping = details;

  // Находим область для детали (определяем до useEffect)
  const findAreaForDetailMemo = useMemo(() => {
    return (detailId: string): ImageArea | null => {
      const detail = allDetailsForMapping.find(d => d.detail === detailId);
      if (!detail) return null;

      const detailName = detail.name.toLowerCase();
      const detailIdLower = detail.detail.toLowerCase();
      const detailGroup = detail.group?.toLowerCase() || '';

      // Определяем, является ли деталь левой или правой
      const isLeft = detailName.includes('left') || detailIdLower.includes('left');
      const isRight = detailName.includes('right') || detailIdLower.includes('right');
      const isFront = detailName.includes('front') || detailIdLower.includes('front');
      const isRear = detailName.includes('rear') || detailName.includes('back') || detailIdLower.includes('rear') || detailIdLower.includes('back');

      // Сначала проверяем поле zone, если оно содержит информацию о view
      if (detail.zone && typeof detail.zone === 'object') {
        const zoneView = (detail.zone as any).view;
        if (zoneView) {
          // Если есть информация о стороне (left/right), учитываем её
          const zoneSide = (detail.zone as any).side;
          let areaByView = IMAGE_AREAS.find(area => {
            if (area.view !== zoneView) return false;
            if (zoneSide === 'left' && !area.id.includes('left') && !area.id.includes('right')) return true;
            if (zoneSide === 'right' && !area.id.includes('left') && !area.id.includes('right')) return true;
            if (zoneSide === 'left' && area.id.includes('left')) return true;
            if (zoneSide === 'right' && area.id.includes('right')) return true;
            return !zoneSide; // Если сторона не указана, берем любую область с нужным view
          });
          if (areaByView) {
            return areaByView;
          }
        }
      }

      // Точное сопоставление по названиям деталей
      // Front bumper и license plate - Front View
      if ((detailName.includes('front bumper') || detailName.includes('front license plate')) && !isLeft && !isRight) {
        return IMAGE_AREAS.find(a => a.id === 'front-view') || null;
      }

      // Grille - Front View
      if (detailName.includes('grille') || detailIdLower.includes('grille')) {
        return IMAGE_AREAS.find(a => a.id === 'front-view') || null;
      }

      // Headlights - различаем левые и правые
      if (detailName.includes('headlight')) {
        if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-front-part') || null;
        if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-front-part') || null;
        // Если не указана сторона, проверяем по view
        return IMAGE_AREAS.find(a => a.id === 'front-view') || null;
      }

      // Turn signals - различаем передние/задние и левые/правые
      if (detailName.includes('turn signal')) {
        if (isFront) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-front-part') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-front-part') || null;
        }
        if (isRear) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-back-part') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-back-part') || null;
        }
      }

      // Hood - Top View
      if (detailName.includes('hood')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Windshield - Top View или Front View (берем Top View, так как он больше)
      if (detailName.includes('windshield')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Fenders - различаем передние/задние и левые/правые
      if (detailName.includes('fender')) {
        if (isFront) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-front-part') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-front-part') || null;
        }
        if (isRear) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-back-part') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-back-part') || null;
        }
      }

      // Mirrors - различаем левые и правые (они на ПЕРЕДНИХ дверях, не задних!)
      if (detailName.includes('mirror')) {
        if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-front-door') || null;
        if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-front-door') || null;
      }

      // Doors - различаем передние/задние и левые/правые
      if (detailName.includes('door') && !detailName.includes('glass')) {
        if (isFront) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-front-door') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-front-door') || null;
        }
        if (isRear) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-back-door') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-back-door') || null;
        }
      }

      // Door glass - различаем передние/задние и левые/правые
      if (detailName.includes('door glass') || detailName.includes('door glass')) {
        if (isFront) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-front-door') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-front-door') || null;
        }
        if (isRear) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-back-door') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-back-door') || null;
        }
      }

      // Rear windshield - Top View или Rear View
      if (detailName.includes('rear windshield')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Taillights и rear turn signals - различаем левые и правые
      if (detailName.includes('taillight') || detailName.includes('tail light') || (detailName.includes('turn signal') && isRear)) {
        if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-back-part') || null;
        if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-back-part') || null;
        // Если не указана сторона, берем Rear View
        return IMAGE_AREAS.find(a => a.id === 'rear-view') || null;
      }

      // Rear view light (lights_reverse) - фонарь заднего хода - Rear View
      if (detailName.includes('rear view light') || detailName.includes('reverse light') || detailIdLower.includes('lights_reverse')) {
        return IMAGE_AREAS.find(a => a.id === 'rear-view') || null;
      }

      // Rear bumper и license plate - Rear View
      if ((detailName.includes('rear bumper') || detailName.includes('rear license plate') || detailName.includes('back bumper') || detailName.includes('back license plate')) && !isLeft && !isRight) {
        return IMAGE_AREAS.find(a => a.id === 'rear-view') || null;
      }

      // Wheels - различаем передние/задние и левые/правые
      if (detailName.includes('wheel') && !detailName.includes('spare')) {
        if (isFront) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-front-part') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-front-part') || null;
        }
        if (isRear) {
          if (isLeft) return IMAGE_AREAS.find(a => a.id === 'left-back-part') || null;
          if (isRight) return IMAGE_AREAS.find(a => a.id === 'right-back-part') || null;
        }
      }

      // Trunk lid - Top View
      if (detailName.includes('trunk')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Roof - Top View
      if (detailName.includes('roof')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Rear seat, rear mat - внутренние детали салона, лучше видны сверху (Top View)
      if (detailName.includes('rear seat') || detailName.includes('rear mat')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Wipers - дворники - Top View (видны сверху)
      if (detailName.includes('wipers') || detailIdLower.includes('wipers')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Brake lights - стоп-сигналы - Rear View
      if (detailName.includes('brake light') || detailIdLower.includes('lights_brake')) {
        return IMAGE_AREAS.find(a => a.id === 'rear-view') || null;
      }

      // Cargo cover - чехол багажника - Top View или Rear View (берем Top View)
      if (detailName.includes('cargo cover') || detailIdLower.includes('cargo_cover')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Spare wheel - запасное колесо - Top View или Rear View (берем Top View, так как обычно видно сверху)
      if (detailName.includes('spare wheel') || detailIdLower.includes('wheel_spare')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // First aid kit - аптечка - внутренняя деталь, Top View
      if (detailName.includes('first aid') || detailIdLower.includes('firstaid')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Fire extinguisher - огнетушитель - внутренняя деталь, Top View
      if (detailName.includes('fire extinguisher') || detailIdLower.includes('extinguisher')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Driver's seat - сиденье водителя - внутренняя деталь, Top View
      if (detailName.includes("driver's seat") || detailName.includes('driver seat') || detailIdLower.includes('seat_driver')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Passenger's seat - сиденье пассажира - внутренняя деталь, Top View
      if (detailName.includes("passenger's seat") || detailName.includes('passenger seat') || detailIdLower.includes('seat_passenger')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Gas cap - крышка бензобака - обычно с левой стороны, но может быть и справа
      // Проверяем по ID или названию, если есть указание на сторону
      if (detailName.includes('gas cap') || detailIdLower.includes('gas_cap')) {
        // По умолчанию привязываем к левой стороне, так как чаще всего бензобак слева
        return IMAGE_AREAS.find(a => a.id === 'left-back-part') || null;
      }

      // Driver's mat - коврик водителя - внутренняя деталь, Top View
      if (detailName.includes("driver's mat") || detailName.includes('driver mat') || detailIdLower.includes('mat_driver')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Passenger's mat - коврик пассажира - внутренняя деталь, Top View
      if (detailName.includes("passenger's mat") || detailName.includes('passenger mat') || detailIdLower.includes('mat_passenger')) {
        return IMAGE_AREAS.find(a => a.id === 'top-view') || null;
      }

      // Fallback: проверяем по ключевым словам (старая логика, но с учетом сторон)
      return IMAGE_AREAS.find(area => {
        // Если область имеет сторону (left/right), проверяем соответствие
        const areaIsLeft = area.id.includes('left');
        const areaIsRight = area.id.includes('right');
        const areaIsFront = area.id.includes('front') || area.view === 'front';
        const areaIsRear = area.id.includes('back') || area.id.includes('rear') || area.view === 'rear';

        // Если деталь имеет сторону, область тоже должна иметь соответствующую сторону
        if (isLeft && areaIsRight) return false;
        if (isRight && areaIsLeft) return false;
        if (isFront && areaIsRear && !area.view.includes('front')) return false;
        if (isRear && areaIsFront && !area.view.includes('rear') && !area.view.includes('back')) return false;

        return area.detailKeywords.some(keyword => {
          const keywordLower = keyword.toLowerCase();
          return (
            detailName.includes(keywordLower) ||
            detailIdLower.includes(keywordLower) ||
            detailGroup.includes(keywordLower)
          );
        });
      }) || null;
    };
  }, [allDetailsForMapping, IMAGE_AREAS]);

  // Отладочная функция для проверки соответствия всех деталей областям
  useEffect(() => {
    if (allDetails.length > 0) {
      // Сначала выводим все данные от сервера
      console.group('📦 Данные деталей от сервера');
      console.log('Всего деталей:', allDetailsForMapping.length);
      allDetailsForMapping.forEach((detail, index) => {
        console.log(`${index + 1}. ${detail.name} (${detail.detail})`, {
          name: detail.name,
          detail: detail.detail,
          group: detail.group,
          zone: detail.zone,
        });
      });
      console.groupEnd();

      // Затем проверяем соответствие областям
      console.group('🔍 Проверка соответствия деталей областям');
      const unmatched: CarTechProfileDetail[] = [];
      const matched: Array<{ detail: CarTechProfileDetail; area: ImageArea }> = [];

      allDetails.forEach(detail => {
        const area = findAreaForDetailMemo(detail.detail);
        if (area) {
          matched.push({ detail, area });
        } else {
          unmatched.push(detail);
        }
      });

      console.log('✅ Найдены области для деталей:', matched.length);
      matched.forEach(({ detail, area }) => {
        console.log(`  ✓ ${detail.name} -> ${area.title}`);
      });

      if (unmatched.length > 0) {
        console.warn('❌ Не найдены области для деталей:', unmatched.length);
        unmatched.forEach(detail => {
          console.warn(`  ✗ ${detail.name} (${detail.detail})`, {
            name: detail.name,
            detail: detail.detail,
            group: detail.group,
            zone: detail.zone,
            zoneStringified: JSON.stringify(detail.zone),
          });
        });
      }

      console.groupEnd();
    }
  }, [allDetails, findAreaForDetailMemo]);

  // Отслеживание изменения размера окна для определения мобильного режима
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Получаем реальный размер изображения
  useEffect(() => {
    const updateImageSize = () => {
      if (imageRef.current && containerRef.current) {
        const img = imageRef.current;
        const naturalWidth = img.naturalWidth || BASE_IMAGE_WIDTH;
        const naturalHeight = img.naturalHeight || BASE_IMAGE_HEIGHT;
        const displayWidth = img.offsetWidth || img.clientWidth;
        const displayHeight = img.offsetHeight || img.clientHeight;
        const rect = img.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();

        setImageSize({ width: naturalWidth, height: naturalHeight });
        setImageDisplaySize({
          width: displayWidth,
          height: displayHeight,
          left: rect.left - containerRect.left,
          top: rect.top - containerRect.top,
        });
      }
    };

    if (imageRef.current) {
      if (imageRef.current.complete) {
        updateImageSize();
      } else {
        imageRef.current.addEventListener('load', updateImageSize);
      }
    }

    const updateDisplaySize = () => {
      if (imageRef.current && containerRef.current) {
        const img = imageRef.current;
        const rect = img.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();

        setImageDisplaySize({
          width: img.offsetWidth || img.clientWidth,
          height: img.offsetHeight || img.clientHeight,
          left: rect.left - containerRect.left,
          top: rect.top - containerRect.top,
        });
      }
    };

    window.addEventListener('resize', updateImageSize);
    window.addEventListener('resize', updateDisplaySize);
    window.addEventListener('scroll', updateDisplaySize, true);

    return () => {
      window.removeEventListener('resize', updateImageSize);
      window.removeEventListener('resize', updateDisplaySize);
      window.removeEventListener('scroll', updateDisplaySize, true);
      if (imageRef.current) {
        imageRef.current.removeEventListener('load', updateImageSize);
      }
    };
  }, []);

  // Функция для поиска деталей, относящихся к конкретной области
  // Использует ту же логику маппинга, что и findAreaForDetailMemo
  const findDetailsForArea = (area: ImageArea): CarTechProfileDetail[] => {
    return allDetailsForMapping.filter(detail => {
      const mappedArea = findAreaForDetailMemo(detail.detail);
      return mappedArea?.id === area.id;
    });
  };

  // Группируем выбранные детали по областям
  const selectedDetailsByArea = useMemo(() => {
    const result: Record<string, { detail: CarTechProfileDetail; index: number }[]> = {};

    console.log('Computing selectedDetailsByArea, selectedDetails:', selectedDetails);
    console.log('allDetailsForMapping count:', allDetailsForMapping.length);

    selectedDetails.forEach((detailId, index) => {
      const detail = allDetailsForMapping.find(d => d.detail === detailId);
      if (!detail) {
        console.warn('Detail not found in allDetailsForMapping:', detailId);
        return;
      }

      const area = findAreaForDetailMemo(detailId);
      if (area) {
        if (!result[area.id]) {
          result[area.id] = [];
        }
        result[area.id].push({ detail, index });
        console.log('Added detail to area:', detail.name, '->', area.title, 'index:', index);
      } else {
        console.warn('No area found for detail:', detail.name, detailId);
      }
    });

    console.log('Result selectedDetailsByArea:', result);
    return result;
  }, [selectedDetails, allDetailsForMapping, findAreaForDetailMemo]);

  // Функция пересчета координат для мобильных устройств
  const scaleCoords = (coords: string, scaleX: number, scaleY: number): string => {
    const coordsArray = coords.split(',').map(Number);
    const scaledCoords: number[] = [];

    for (let i = 0; i < coordsArray.length; i += 2) {
      scaledCoords.push(Math.round(coordsArray[i] * scaleX));
      if (i + 1 < coordsArray.length) {
        scaledCoords.push(Math.round(coordsArray[i + 1] * scaleY));
      }
    }

    return scaledCoords.join(',');
  };

  // Получаем центр области для позиционирования кружка
  const getAreaCenter = (coords: string): { x: number; y: number } => {
    const coordsArray = coords.split(',').map(Number);
    let sumX = 0;
    let sumY = 0;
    for (let i = 0; i < coordsArray.length; i += 2) {
      sumX += coordsArray[i];
      sumY += coordsArray[i + 1];
    }
    return {
      x: sumX / (coordsArray.length / 2),
      y: sumY / (coordsArray.length / 2),
    };
  };

  const handleAreaClick = (e: React.MouseEvent<HTMLAreaElement>, area: ImageArea) => {
    e.preventDefault();
    const detailsForArea = findDetailsForArea(area);

    if (detailsForArea.length === 0) return;

    if (detailsForArea.length === 1) {
      // Если только одна деталь, выбираем её сразу
      const detail = detailsForArea[0];
      if (selectedDetails.includes(detail.detail)) {
        onDetailDeselect(detail.detail);
      } else {
        onDetailSelect(detail.detail);
      }
      setFilteredArea(null); // Сбрасываем фильтр
    } else {
      // Если несколько деталей, фильтруем список справа
      setFilteredArea(area.id);
    }
  };

  const handleAreaMouseEnter = (area: ImageArea) => {
    setHoveredArea(area.id);
  };

  const handleAreaMouseLeave = () => {
    setHoveredArea(null);
  };

  const handleDetailMouseEnter = (detailId: string) => {
    setHoveredDetail(detailId);
    const area = findAreaForDetailMemo(detailId);
    if (area) {
      setHoveredArea(area.id);
    }
  };

  const handleDetailMouseLeave = () => {
    setHoveredDetail(null);
    setHoveredArea(null);
  };

  const handleDetailClick = (detailId: string) => {
    if (selectedDetails.includes(detailId)) {
      onDetailDeselect(detailId);
    } else {
      onDetailSelect(detailId);
    }
  };

  const handleCircleClick = (detailId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDetailFocus) {
      onDetailFocus(detailId);
    }
  };

  // Конвертируем координаты в SVG path
  const getPathData = (coordsString: string): string => {
    const coords = coordsString.split(',').map(Number);
    if (coords.length < 4) return '';

    let path = `M ${coords[0]} ${coords[1]}`;
    for (let i = 2; i < coords.length; i += 2) {
      if (i + 1 < coords.length) {
        path += ` L ${coords[i]} ${coords[i + 1]}`;
      }
    }
    path += ' Z';
    return path;
  };

  // Фильтруем детали для списка справа
  const filteredDetails = useMemo(() => {
    let details = allDetails;

    // Сначала фильтруем по области, если выбрана
    if (filteredArea) {
      const area = IMAGE_AREAS.find(a => a.id === filteredArea);
      if (area) {
        details = findDetailsForArea(area);
      }
    }

    // Затем фильтруем по поисковому запросу
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      details = details.filter(detail => {
        const detailName = detail.name.toLowerCase();
        const detailId = detail.detail.toLowerCase();
        const detailGroup = detail.group?.toLowerCase() || '';
        return (
          detailName.includes(query) ||
          detailId.includes(query) ||
          detailGroup.includes(query)
        );
      });
    }

    return details;
  }, [filteredArea, searchQuery, allDetails]);

  // Мемоизированные пересчитанные координаты для мобильных устройств
  const scaledAreas = useMemo(() => {
    if (!isMobile || !imageDisplaySize || !imageSize) {
      return IMAGE_AREAS; // На десктопе используем оригинальные координаты
    }

    const scaleX = imageDisplaySize.width / imageSize.width;
    const scaleY = imageDisplaySize.height / imageSize.height;

    return IMAGE_AREAS.map(area => ({
      ...area,
      coords: scaleCoords(area.coords, scaleX, scaleY)
    }));
  }, [isMobile, imageDisplaySize, imageSize, IMAGE_AREAS]);

  const currentImageSize = imageSize || { width: BASE_IMAGE_WIDTH, height: BASE_IMAGE_HEIGHT };

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className="flex-1 w-full md:w-auto">
          <div className="relative bg-gray-50 rounded-lg" ref={containerRef} style={{ overflow: 'visible' }}>
            <map name="car-image-map" ref={mapRef} id="car-image-map">
              {scaledAreas.map((area) => {
                const detailsForArea = findDetailsForArea(area);
                const hasDetails = detailsForArea.length > 0;

                return (
                  <area
                    key={area.id}
                    alt={area.title}
                    title={area.title}
                    coords={area.coords}
                    shape="poly"
                    onClick={(e) => handleAreaClick(e, area)}
                    onMouseEnter={() => handleAreaMouseEnter(area)}
                    onMouseLeave={handleAreaMouseLeave}
                    style={{ cursor: hasDetails ? 'pointer' : 'default' }}
                  />
                );
              })}
            </map>

            <div className="relative flex justify-start items-center">
              <img
                ref={imageRef}
                src="/images/car_schema.png"
                alt="Car schema"
                useMap="#car-image-map"
                className="max-w-full h-auto"
                style={{
                  width: 'auto',
                  height: 'auto',
                  display: 'block',
                }}
                draggable={false}
                onLoad={() => {
                  if (imageRef.current && containerRef.current) {
                    const img = imageRef.current;
                    const naturalWidth = img.naturalWidth || BASE_IMAGE_WIDTH;
                    const naturalHeight = img.naturalHeight || BASE_IMAGE_HEIGHT;
                    const rect = img.getBoundingClientRect();
                    const containerRect = containerRef.current.getBoundingClientRect();

                    setImageSize({ width: naturalWidth, height: naturalHeight });
                    setImageDisplaySize({
                      width: img.offsetWidth || img.clientWidth,
                      height: img.offsetHeight || img.clientHeight,
                      left: rect.left - containerRect.left,
                      top: rect.top - containerRect.top,
                    });
                  }
                }}
              />

              {/* SVG overlay для визуализации при наведении и кружки для выбранных деталей */}
              {imageDisplaySize && (
                <svg
                  className="absolute"
                  style={{
                    left: `${imageDisplaySize.left}px`,
                    top: `${imageDisplaySize.top}px`,
                    width: `${imageDisplaySize.width}px`,
                    height: `${imageDisplaySize.height}px`,
                    zIndex: 20,
                    pointerEvents: 'none',
                  }}
                  viewBox={`0 0 ${currentImageSize.width} ${currentImageSize.height}`}
                  preserveAspectRatio="xMidYMid meet"
                >
                  {/* Подсветка областей при наведении */}
                  {IMAGE_AREAS.map((area) => {
                    const isHovered = hoveredArea === area.id;
                    const hasSelected = selectedDetailsByArea[area.id]?.length > 0;

                    if (!isHovered && !hasSelected) return null;

                    const pathData = getPathData(area.coords);

                    return (
                      <path
                        key={`overlay-${area.id}`}
                        d={pathData}
                        fill={hasSelected ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.4)'}
                        stroke={hasSelected ? '#2563eb' : '#3b82f6'}
                        strokeWidth={hasSelected ? '3' : '2'}
                        strokeDasharray={hasSelected ? '0' : '5'}
                      />
                    );
                  })}

                  {/* Зеленые кружки с номерами для выбранных деталей (как в референсе) */}
                  {selectedDetails.length > 0 && (() => {
                    console.log('🎯 Rendering circles check:', {
                      selectedDetailsCount: selectedDetails.length,
                      selectedDetailsByAreaKeys: Object.keys(selectedDetailsByArea),
                      selectedDetailsByArea,
                      imageDisplaySize,
                      currentImageSize,
                    });
                    return null;
                  })()}
                  {selectedDetails.length > 0 && Object.entries(selectedDetailsByArea).map(([areaId, detailsInArea]) => {
                    const area = IMAGE_AREAS.find(a => a.id === areaId);
                    if (!area || !detailsInArea || detailsInArea.length === 0) {
                      console.warn('⚠️ Skipping circle render:', { areaId, area, detailsInArea });
                      return null;
                    }

                    const center = getAreaCenter(area.coords);
                    const totalCircles = detailsInArea.length;
                    const circleRadius = 14;
                    const circleSpacing = 32; // Расстояние между центрами кружков

                    // Вычисляем начальную позицию для размещения кружков в ряд
                    // Если кружков несколько, располагаем их горизонтально
                    const startX = totalCircles === 1
                      ? center.x
                      : center.x - ((totalCircles - 1) * circleSpacing) / 2;

                    console.log('📍 Rendering circles for area:', area.title, {
                      center,
                      detailsCount: totalCircles,
                      startX,
                      viewBox: `0 0 ${currentImageSize.width} ${currentImageSize.height}`,
                    });

                    return detailsInArea.map(({ detail, index }) => {
                      const circleId = `circle-${detail.detail}-${index}`;

                      // Позиция кружка: если один - в центре, если несколько - в ряд
                      const circleX = totalCircles === 1
                        ? center.x
                        : startX + (index * circleSpacing);
                      const circleY = center.y;

                      return (
                        <g
                          key={circleId}
                          style={{ pointerEvents: 'auto' }}
                        >
                          <circle
                            id={circleId}
                            cx={circleX}
                            cy={circleY}
                            r="14"
                            fill="#10b981"
                            stroke="#ffffff"
                            strokeWidth="3"
                            style={{
                              pointerEvents: 'auto',
                              cursor: 'pointer',
                              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCircleClick(detail.detail, e);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.setAttribute('r', '16');
                              e.currentTarget.setAttribute('fill', '#059669');
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.setAttribute('r', '14');
                              e.currentTarget.setAttribute('fill', '#10b981');
                            }}
                          />
                          <text
                            x={circleX}
                            y={circleY}
                            fill="white"
                            stroke="white"
                            strokeWidth="0.5"
                            textAnchor="middle"
                            alignmentBaseline="middle"
                            fontSize="12"
                            fontWeight="bold"
                            style={{
                              pointerEvents: 'none',
                              userSelect: 'none',
                            }}
                          >
                            {index + 1}
                          </text>
                          <title>{detail.name} ({index + 1})</title>
                        </g>
                      );
                    });
                  })}
                </svg>
              )}

            </div>
          </div>
        </div>

        {/* Список деталей справа (снизу на мобильных) */}
        <div className="w-full md:w-80 flex-shrink-0">
          <div
            className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col"
            style={{
              height: imageDisplaySize ? `${imageDisplaySize.height}px` : 'auto',
              minHeight: '400px',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">
                {filteredArea
                  ? `Details (${filteredDetails.length})`
                  : `Available Details (${allDetails.length})`
                }
              </h3>
              {(filteredArea || searchQuery) && (
                <button
                  data-testid="interactive-car-image-clear-filters"
                  onClick={() => {
                    setFilteredArea(null);
                    setSearchQuery('');
                  }}
                  className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                  title="Clear filters"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Clear
                </button>
              )}
            </div>

            {/* Поле поиска */}
            <div className="mb-3">
              <input
                data-testid="interactive-car-image-search"
                type="text"
                placeholder="Search details..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {filteredArea && (
              <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                Showing details for: <strong>{IMAGE_AREAS.find(a => a.id === filteredArea)?.title}</strong>
              </div>
            )}
            <div className="space-y-2 overflow-y-auto flex-1">
              {filteredDetails.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No details available
                </div>
              ) : (
                filteredDetails.map((detail) => {
                  const isSelected = selectedDetails.includes(detail.detail);
                  const isHovered = hoveredDetail === detail.detail;

                  return (
                    <button
                      key={detail.detail}
                      data-testid={`interactive-car-image-detail-${detail.detail}`}
                      onClick={() => {
                        handleDetailClick(detail.detail);
                        // Если деталь выбрана, сбрасываем фильтр
                        if (!isSelected) {
                          setFilteredArea(null);
                        }
                      }}
                      onMouseEnter={() => handleDetailMouseEnter(detail.detail)}
                      onMouseLeave={handleDetailMouseLeave}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors border ${
                        isSelected
                          ? 'bg-primary text-white border-primary'
                          : isHovered
                          ? 'bg-blue-50 text-gray-900 border-blue-200'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {detail.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveCarImage;
